/* SpawnAnything — replay player UI: playback bar (play/pause, speed 0.25–4×, seek with progress, kino/free camera),
   saving and opening .awreplay files (button, drag & drop), plus the replay/clip buttons in the battle bar and
   the victory banner. The playback itself runs in the sim worker (replay mode); main.js passes `game`. */
import '../ui/replay.css';
import { encodeReplay, decodeReplay, replayFileName, REPLAY_EXT, REPLAY_MIME } from './format.js';
import { downloadBlob, canShareFile, shareFile, fmtBytes } from '../ui/share.js';

const SPEEDS = [0.25, 0.5, 1, 2, 4];
const clock = s => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const el = (html) => { const t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild; };

export function createReplayPlayer(game) {
  let rep = null, step = 0, ended = false, seeking = null, dragging = false, view = 1;

  /* ---- DOM ---- */
  const bar = el(`<nav class="aw-rpbar" hidden aria-label="Wiederholung">
    <div class="aw-rphead"><span class="aw-rptag">Wiederholung</span><b class="aw-rpname"></b></div>
    <div class="aw-rprow">
      <button data-a="play" class="aw-rpplay">Pause</button>
      <span class="aw-rptime">0:00</span>
      <input type="range" class="aw-rpseek" min="0" max="1" step="1" value="0" aria-label="Spulen">
      <span class="aw-rpend">0:00</span>
    </div>
    <div class="aw-rprow aw-rpbtns">
      ${SPEEDS.map(v => `<button data-v="${v}" class="${v === 1 ? 'on' : ''}">${String(v).replace('.', ',')}×</button>`).join('')}
      <span class="sep"></span>
      <button data-a="kino" aria-pressed="false">Kino-Kamera</button>
      <button data-a="clip" title="Die letzten 15 Sekunden bis hier als Hochkant-Video (Taste C)">Clip</button>
      <button data-a="save" title="Wiederholung als Datei speichern">Speichern</button>
      <button data-a="close">Schließen</button>
    </div>
  </nav>`);
  const prog = el(`<div class="aw-rpprog" hidden role="status"><span>Spule …</span><i><b></b></i><em>0 %</em></div>`);
  const drop = el(`<div class="aw-drop" hidden><div>Wiederholung hier ablegen<small>${REPLAY_EXT}-Datei</small></div></div>`);
  const file = el(`<input type="file" accept="${REPLAY_EXT},${REPLAY_MIME},application/gzip,application/json" hidden>`);
  document.body.append(bar, prog, drop, file);
  const $ = s => bar.querySelector(s);
  const seekEl = $('.aw-rpseek');

  // setup panel: open a file; battle bar + victory banner: clip, save, watch
  const start = document.querySelector('#start');
  if (start) start.insertAdjacentElement('afterend', el(`<button class="ghost aw-rpopen" title="Eine gespeicherte Wiederholung (${REPLAY_EXT}) abspielen – oder die Datei einfach ins Fenster ziehen">Wiederholung öffnen</button>`));
  const ctrl = document.querySelector('#ctrl');
  if (ctrl) {
    const edit = ctrl.querySelector('#edit');
    const add = [el(`<button data-a="clip" title="Die letzten 15 Sekunden als Hochkant-Video (Taste C)">Clip</button>`), el(`<button data-a="save" title="Wiederholung als Datei speichern (${REPLAY_EXT})">Speichern</button>`)];
    for (const b of add) edit ? ctrl.insertBefore(b, edit) : ctrl.append(b);
  }
  const bbtn = document.querySelector('#banner .bbtn');
  if (bbtn) bbtn.append(el(`<button data-a="watch">Wiederholung ansehen</button>`), el(`<button data-a="clip">Clip</button>`), el(`<button data-a="save">Speichern</button>`));

  const actions = {
    play() { if (!rep) return; if (ended) { seek(0); game.setPaused(false); } else game.setPaused(!game.paused); refresh(); },
    kino() { game.setKino(!game.kino); refresh(); },
    clip() { game.clip(); },
    save() { save(); },
    close() { game.exitReplay(); },
    watch() { const r = game.recorder.build(); if (!r || !r.ticks) { game.toast('Noch nichts aufgenommen.'); return; } open(r); },
    open() { file.value = ''; file.click(); },
  };
  document.addEventListener('click', e => {
    const b = e.target.closest('[data-a]');
    if (b && (bar.contains(b) || b.closest('#ctrl') || b.closest('#banner'))) actions[b.dataset.a] && actions[b.dataset.a]();
    else if (e.target.closest('.aw-rpopen')) actions.open();
  });
  bar.querySelectorAll('[data-v]').forEach(b => b.addEventListener('click', () => setView(+b.dataset.v)));
  seekEl.addEventListener('input', () => { dragging = true; $('.aw-rptime').textContent = clock(+seekEl.value / 30); });
  seekEl.addEventListener('change', () => { dragging = false; seek(+seekEl.value); });
  file.addEventListener('change', () => { if (file.files && file.files[0]) openFile(file.files[0]); });

  // drag & drop anywhere
  let dragN = 0;
  const hasFiles = e => e.dataTransfer && [...(e.dataTransfer.types || [])].includes('Files');
  addEventListener('dragenter', e => { if (!hasFiles(e) || game.clipBusy) return; e.preventDefault(); dragN++; drop.hidden = false; });
  addEventListener('dragover', e => { if (!hasFiles(e)) return; e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; });
  addEventListener('dragleave', e => { if (!hasFiles(e)) return; if (--dragN <= 0) { dragN = 0; drop.hidden = true; } });
  addEventListener('drop', e => {
    if (!hasFiles(e)) return; e.preventDefault(); dragN = 0; drop.hidden = true;
    const f = e.dataTransfer.files && e.dataTransfer.files[0]; if (f) openFile(f);
  });

  /* ---- worker messages (forwarded by main.js) ---- */
  game.listen(m => {
    if (game.clipBusy || game.state !== 'replay') return;
    if (m.type === 'frame') { if (m.step != null) { step = m.step; if (!seeking) showPos(); } }
    else if (m.type === 'rpprog' && seeking) showProg((m.step - seeking.from) / Math.max(1, m.to - seeking.from));
    else if (m.type === 'rpseeked') { seeking = null; prog.hidden = true; step = m.step; ended = step >= rep.ticks; showPos(); refresh(); }
    else if (m.type === 'rpreset') game.resetVisuals();
    else if (m.type === 'rpend') { ended = true; refresh(); }
    else if (m.type === 'rpdesync') game.toast('Achtung: Diese Wiederholung läuft anders ab als aufgenommen (andere Spielversion?).');
  });

  function setView(v) {
    view = v; game.setView(v);
    bar.querySelectorAll('[data-v]').forEach(b => b.classList.toggle('on', +b.dataset.v === v));
  }
  function seek(to) {
    if (!rep) return;
    to = Math.max(0, Math.min(rep.ticks, to | 0));
    seeking = { from: to < step ? 0 : step, to };
    ended = false;
    game.post({ cmd: 'rpseek', to });
    showProg(0);
  }
  let progT = 0;
  function showProg(f) {
    // only show the pill when the seek takes a moment
    if (prog.hidden) { if (!progT) progT = setTimeout(() => { progT = 0; if (seeking) prog.hidden = false; }, 250); }
    const p = Math.round(Math.max(0, Math.min(1, f)) * 100);
    prog.querySelector('b').style.width = p + '%'; prog.querySelector('em').textContent = p + ' %';
  }
  function showPos() {
    if (!rep) return;
    if (!dragging) { seekEl.value = String(step); $('.aw-rptime').textContent = clock(step / 30); }
  }
  function refresh() {
    if (!rep) return;
    $('.aw-rpplay').textContent = ended ? 'Nochmal' : game.paused ? 'Abspielen' : 'Pause';
    $('[data-a="kino"]').setAttribute('aria-pressed', String(game.kino));
  }
  setInterval(() => { if (!bar.hidden) refresh(); }, 250);

  /* ---- open / save ---- */
  function open(r) {
    if (game.clipBusy) return;
    rep = r; step = 0; ended = false; seeking = null; view = 1;
    try { game.enterReplay(r); } catch (e) { console.error(e); game.toast('Die Wiederholung konnte nicht geladen werden.'); rep = null; return; }
    setView(1);
    $('.aw-rpname').textContent = r.title || '';
    seekEl.max = String(Math.max(1, r.ticks)); $('.aw-rpend').textContent = clock(r.ticks / 30);
    showPos(); refresh(); sync();
    game.toast(`Wiederholung: ${r.title || 'Schlacht'} (${clock(r.ticks / 30)})`);
  }
  async function openFile(f) {
    try {
      if (f.size > 64 * 1048576) throw new Error('Die Datei ist zu groß für eine Wiederholung.');
      open(await decodeReplay(new Uint8Array(await f.arrayBuffer())));
    } catch (e) { game.toast(e && e.message ? e.message : 'Die Datei konnte nicht geöffnet werden.'); }
  }
  async function save() {
    const r = game.state === 'replay' ? rep : game.recorder.build();
    if (!r || !r.ticks) { game.toast('Noch nichts aufgenommen: starte zuerst eine Schlacht.'); return; }
    const bytes = await encodeReplay(r), name = replayFileName(r.title);
    const f = new File([bytes], name, { type: REPLAY_MIME });
    // phones: the share sheet (messenger, cloud); desktop: download
    if (matchMedia('(pointer: coarse)').matches && canShareFile(f)) { try { if (await shareFile(f, r.title, 'SpawnAnything – Wiederholung')) return; } catch (e) { /* fall back to the download */ } }
    downloadBlob(f, name);
    game.toast(`Wiederholung gespeichert (${fmtBytes(bytes.length)}, ${clock(r.ticks / 30)})`);
  }

  /** show/hide the bar for the current game state (called by main.js updateUIState) */
  function sync() {
    const on = game.state === 'replay';
    bar.hidden = !on;
    if (!on) { seeking = null; prog.hidden = true; }
    document.body.classList.toggle('aw-replay', on);
  }

  return {
    sync, open, openFile, save,
    /** the replay being watched, cut at the current position (for clips) */
    current() { return rep ? { rep, end: Math.min(step, rep.ticks) } : null; },
  };
}
